<template>
    <div class="tierWrap">
        <div class="emptyHeader" v-if="showEmptyTier">
            <div class="tierTypeBubble font-6">
                <span class="tierName">Tier {{tier.position}}</span>
            </div>
            <div class="dragArea font-9" :class="!isDragging ? ['dragAreaText'] : ['backgroundDrag', 'primary']"
                 v-show="!disableChangeTier">
                Drag and drop ad sources
            </div>
        </div>
        <div v-else>
            <div class="header waffle font-6">
                <div class="tierTypeBubble" :class="{'onHover': !disableChangeTier}">
                    <span class="tierName">Tier {{tier.position}}:</span>
                    <Dropdown :unique-id="typeDropdownId"
                              :options="typeOptions"
                              dropdownStyle="radio"
                              :selected-id="tier.type"
                              class="tierTypeDropdown"
                              optionsTitle="Change type to:"
                              :isDisable="viewOnly || disableChangeTier"
                              height="24"
                              @dropdownSelectedItemChanged="typeChanged"
                    />
                </div>
                <div class="headerTitle">
                    Rate
                    <v-popover class="rateTooltip"
                               trigger="hover"
                               placement="top"
                               delay="500"
                               popoverInnerClass="tooltip-inner"
                               popoverBaseClass="vueTooltip"
                               popoverArrowClass="tooltip-arrow">
                        <IconBase
                                width="10"
                                height="10"
                                :icon-color="consts.COLORS.LIGHT_700"
                                icon-name="infoTooltipIcon"/>
                        <template slot="popover">
                            Rate is used only to prioritize the instance in your waterfall.
                            This value is not sent back to the network
                            <a :href="rateKCLink" target="_blank">Read more</a>
                        </template>
                    </v-popover>
                </div>
                <div class="headerTitle">
                    eCPM
                </div>
                <div class="headerTitle">
                    Imps.
                </div>
                <div class="headerTitle">
                    SOV
                    <Tooltip text="SOV (share of voice) reflects this network instance revenue share compared to total
                     revenue from both bidding and non-bidding ad source"/>
                </div>
            </div>
        </div>
        <div v-if="tier.instances && tier.instances.length > 0"
             v-show="showDragAndDropMessage" class="draggingWrapper backgroundDrag">
            <div class="font-9 primary">
                Drag and drop ad sources
            </div>
        </div>
        <Draggable v-model="tier.instances"
                   group="instancesGroup"
                   @change="instanceDragAndDropChange($event, tier)"
                   v-bind="dragOptions"
                   @start="onDragStart"
                   @end="onDragEnd"
                   :move="checkMove"
                   :sort="isTierTypeManual"
                   :componentData="componentData()"
        >
            <transition-group :name="transitionGroup"
                              :class="{'emptyDraggable': showEmptyTier}">
                <div v-for="instance in tier.instances"
                     :key="`${instance.providerId}_${instance.instanceId}_${instance.tagId}`"
                     :class="{
                        'dragCursor': !disableChangeTier && !disableUpdate,
                        'disabledTier': disableUpdate,
                        'instanceBackgroundAfterDrag':
                            showBackgroundAfterDrag(instance.providerId, instance.instanceId, instance.tagId),
                    }">
                    <InstanceWaffle
                            @rateChanged="onRateChange"
                            @menuAction="onMenuAction"
                            :rates="instance.rates"
                            :providerName="instance.providerDisplayName"
                            :name="instance.instanceName"
                            :showMenu="!countryLevel || !disableChangeTier"
                            :impressions="instance.impressions"
                            :ecpm="instance.eCPM"
                            :activeCountries="instance.activeCountries"
                            :isAllCountries="instance.isAllCountries"
                            :counter="getCounter(instance)"
                            :capping="instance.iterationAdCap"
                            :tierColor="tierColor"
                            :tagType="instance.tagType"
                            :tagId="instance.tagId"
                            :instanceId="instance.instanceId"
                            :aniId="instance.aniId"
                            :providerId="instance.providerId"
                            :appKey="appKey"
                            :viewOnly="viewOnly"
                            :allowRemoveFromGroup="allowRemoveFromGroup"
                            :missingConfiguration="instance.isMissingConfig"
                            :tierId="tier.id"
                            :class="{'draggingOverNonManual': !isTierTypeManual && isDragging}"
                            :countryLevel="countryLevel"
                            class="waffle"
                            :isLocked="disableChangeTier"
                            :tierIsUpdating="tierIsUpdating"
                            :showCappingInMenu="adUnit !== banner"
                            :onUpdate="isInstanceOnUpdate(instance.providerId, instance.instanceId, instance.tagId)"
                            :showChinaCountriesTooltip="showChinaCountriesTooltip(instance)"
                            :sov="{'val': instance.sov, 'maxVal': maxSov}"
                    />
                </div>
            </transition-group>
        </Draggable>
    </div>
</template>

<script>

import Vue from 'vue';
import Draggable from 'vuedraggable';
import Dropdown from 'components/dropdowns/Dropdown.vue';
import IconBase from 'components/IconBase.vue';
import DragMoveService from 'services/dragDrop/dragMoveService';
import Links from 'services/metaData/links';
import consts from 'services/metaData/consts';
import tooltip from 'services/libWrappers/vue-tooltip';
import Tooltip from 'components/Tooltip/Tooltip.vue';
import InstanceWaffle from '../InstanceWaffle/InstanceWaffle.vue';
import tierConsts from '../../consts';

tooltip(Vue);

const dragMoveService = new DragMoveService();

export default {
    name: 'Tier',
    data: () => {
        const options = [];
        tierConsts.TIER_TYPE_ORDER
            .forEach((typeId) => {
                options.push({
                    id: typeId,
                    displayText: tierConsts.TIER_TYPE_TO_NAME[typeId]
                });
            });
        return {
            typeOptions: options,
            consts,
            tierConsts,
            dragStartedOnThisTier: false,
            transitionGroup: tierConsts.dragAndDropAnimation,
            emptyTier: [],
            dragDistanceFromBoundsY: 0
        };
    },
    props: {
        tier: {
            type: Object,
            default() {
                return {
                    data: {}
                };
            },
        },
        countryLevel: Boolean,
        hasActiveBidders: Boolean,
        viewOnly: Boolean,
        startCounter: {
            type: Number,
            default: 0
        },
        appKey: String,
        allowRemoveFromGroup: Boolean,
        isDragging: Boolean,
        disableChangeTier: Boolean,
        tierIsUpdating: Boolean,
        instanceKeyToUpdate: String,
        parentComponent: HTMLElement,
        groupHasChina: Boolean,
        adUnit: String,
        maxSov: Number
    },
    watch: {
        hasActiveBidders: {
            immediate: true,
            handler(newVal) {
                const optimized = this.typeOptions.filter(
                    // eslint-disable-next-line eqeqeq
                    (option) => option.id == tierConsts.OPTIMIZED
                )[0];
                optimized.disabled = newVal;
                optimized.tooltip = newVal ? 'When bidding is active, the Optimized tier type will be deactivated' : '';
            }
        }
    },
    methods: {
        typeChanged(id) {
            this.$emit('typeChanged', id);
        },
        onRateChange(rate) {
            const params = { ...rate, tierId: this.tier.id, groupId: this.tier.groupId };
            this.transitionGroup = tierConsts.rateAnimation;
            this.$emit('rateChanged', params);
        },
        async onMenuAction(data) {
            this.$emit('menuAction', data);
        },
        getCounter(instance) {
            return instance.manualPosition ? this.startCounter + instance.manualPosition : null;
        },
        instanceDragAndDropChange(event, tier) {
            const { added, moved } = event;

            // only trigger for added or moved in manual
            if (!added && !moved) {
                return;
            }
            if (moved && !this.isTierTypeManual) {
                // return instance to its previous position
                // eslint-disable-next-line prefer-const
                let { newIndex, oldIndex } = moved;
                if (newIndex >= this.tier.instances.length) {
                    newIndex = this.tier.instances.length - 1;
                }
                if (newIndex !== oldIndex) {
                    this.tier.instances.splice(oldIndex, 0, this.tier.instances.splice(newIndex, 1)[0]);
                }
                return;
            }

            const draggedInstance = added || moved;
            const { element: instance, newIndex } = draggedInstance;
            const manualPosition = this.isTierTypeManual ? newIndex + 1 : null;

            this.$emit('instancePositionChanged', { instance, manualPosition, tierId: tier.id });
        },
        onDragStart({ originalEvent }) {
            this.transitionGroup = tierConsts.dragAndDropAnimation;
            dragMoveService.isEntityDragActive = true;
            dragMoveService.dragMove(originalEvent, this.parentComponent);
            this.dragStartedOnThisTier = true;
            this.$emit('tierOnDrag', this.tier.id);
            document.addEventListener('mousemove', this.handleMove);
            this.calcDistanceFromBounds();
        },
        onDragEnd() {
            dragMoveService.isEntityDragActive = false;
            this.dragStartedOnThisTier = false;
            this.$emit('tierOnDrag', null);
            document.removeEventListener('mousemove', this.handleMove)
            this.dragDistanceFromBoundsY = 0;
        },
        checkMove({ relatedContext, originalEvent }) {
            const { tierId } = relatedContext.component.componentData;
            this.$emit('tierOnDrag', tierId);

            return true;
        },
        calcDistanceFromBounds() {
            const draggedElement = this.parentComponent.querySelector('.sortable-chosen.drag');
            let parentBounds = this.parentComponent.getBoundingClientRect();
            let elemBounds = draggedElement.getBoundingClientRect();
            this.dragDistanceFromBoundsY = parentBounds.y - elemBounds.y;
        },
        handleMove(event) {
            dragMoveService.dragMove(event, this.parentComponent);
            const mouseY = event.clientY;
            const mouseBuffer = 10;
            const containerHorizontalPadding = 17;
            const draggedElement = this.parentComponent.querySelector('.sortable-chosen.drag');
            let transform = draggedElement.style.transform;
            transform = transform.replace('matrix', '').replace('(', '').replace(')', '');
            let parts = transform.split(',');
            let translationX = parts[4];
            let translationY = parts[5];
            if (translationX < -containerHorizontalPadding) {
                translationX = -containerHorizontalPadding;
            }
            if (translationX > 0) {
                translationX = 0;
            }
            let parentBounds = this.parentComponent.getBoundingClientRect();
            let elemBounds = draggedElement.getBoundingClientRect();
            let outOfBoundsTop = parentBounds.y - elemBounds.y;
            let outOfBoundsBottom = (parentBounds.y + parentBounds.height) - elemBounds.y;
            if (outOfBoundsTop > 0 || (mouseY - mouseBuffer) < parentBounds.y) {
                translationY = this.dragDistanceFromBoundsY;
            } else if (outOfBoundsBottom < 0) {
                translationY = parseInt(translationY) + outOfBoundsBottom;
            }

            draggedElement.style.transform = `matrix(${parts[0]},${parts[1]},${parts[2]},${parts[3]},${translationX},${translationY})`;
        },
        componentData() {
            return {
                tierId: this.tier.id
            };
        },
        showBackgroundAfterDrag(providerId, instanceId, tagId) {
            return this.isInstanceOnUpdate(providerId, instanceId, tagId) && this.isTierTypeManual;
        },
        isInstanceOnUpdate(providerId, instanceId, tagId) {
            return `${providerId}_${instanceId}_${tagId}` === this.instanceKeyToUpdate && this.tierIsUpdating;
        },
        showChinaCountriesTooltip({ providerId }) {
            return this.groupHasChina && providerId === consts.PROVIDER_IDS.FACEBOOK;
        }
    },
    computed: {
        banner() {
            return tierConsts.banner;
        },
        typeDropdownId() {
            return `tierType${this.tier.id}`;
        },
        tierColor() {
            return tierConsts.TIER_POS_TO_COLOR[this.tier.position];
        },
        rateKCLink() {
            return Links.rate;
        },
        disableUpdate() {
            return this.tierIsUpdating;
        },
        isTierTypeManual() {
            return this.tier.type === tierConsts.MANUAL;
        },
        dragOptions() {
            return {
                ghostClass: !this.isTierTypeManual
                    || (this.isTierTypeManual && this.tier.instances && this.tier.instances.length === 0)
                    ? 'ghostOnNonManual' : 'ghostOnManual',
                dragClass: 'drag',
                disabled: this.disableChangeTier || this.disableUpdate,
                scrollSensitivity: 200,
                forceFallback: true,
                filter: '.disableDrag'
            };
        },
        showEmptyTier() {
            return !this.tier.instances || this.tier.instances.length === 0
                || (this.tier.instances.length === 1 && this.dragStartedOnThisTier);
        },
        showDragAndDropMessage() {
            return this.isDragging && !this.isTierTypeManual
                && !(this.dragStartedOnThisTier && this.tier.instances && this.tier.instances.length === 1);
        }
    },
    components: {
        Dropdown,
        InstanceWaffle,
        IconBase,
        Draggable,
        Tooltip
    }
};
</script>

<style scoped lang="scss">
    @import "~styles/styleGuide";

    .tierWrap {
        position: relative;;
    }

    .tierName {
        display: inline-block;
    }

    .tierTypeBubble{
        height: 24px;
        width: fit-content;
        line-height: 24px;
        padding-left: 8px;
        border-radius: 12px;
        background-color: $light-300;
        margin-top: 8px;

        &.onHover {
            &:hover {
                background-color: $light-400;
            }
        }
    }

    .tierTypeDropdown {
        margin-left: 5px;
    }

    .rateAnimation-move{
        transition: all 800ms ease;
    }

    .waffle {
        border-bottom: 1px solid $light-500;
        border-top: 1px solid $light-500;
        margin-top: -1px;
    }

    .header {
        display: grid;
        justify-content: space-between;
        grid-template-columns: 2.9fr 1.1fr 1fr 1fr .9fr;
        grid-gap: 15px;
        grid-template-rows: 40px;
        line-height: 40px;
    }

    .headerTitle {
        color: $light-700;
        padding-left: 7px;
    }

    .emptyHeader {
        display: grid;
        justify-content: stretch;
        grid-template-columns: max-content auto;
        grid-template-rows: 24px;
        line-height: 22px;
        padding: 8px 0px;
        border-bottom: 1px solid $light-500;

        .tierName {
            padding-right: 9px;
        }

        .tierTypeBubble {
            margin-top: 0;
            margin-right: 10px;
        }
    }

    .dragArea {
        border-radius: 20px;
        border: dashed 1px $light-600;
        padding-right: 5px;
        padding-left: 5px;
        text-align: center;
    }

    .dragAreaText {
        color: $light-700;
    }

    .rateTooltip {
        display: inline-block;
        line-height: 10px
    }
    .ghostOnNonManual {
        visibility: hidden;
        height: 0;
    }

    .ghostOnManual {
        $width: 168px;
        padding: 4px 0;

        & div {
            display: none;
        }
        &:after {
            border-radius: 16px;
            padding: 4px 8px 5px 8px;
            text-align: center;
            width: $width;
            margin-left: calc(50% - (#{$width} / 2) - 10px);
            margin-top: 8px;
            margin-bottom: 8px;
            display: block;
            @extend .backgroundDrag;
            @extend .font-9;
            @extend .primary;
            content: 'Drag and drop ad sources';
        }
    }

    .drag {
        background: $light-100;
        cursor: grabbing;
        height: 41px !important;
        /deep/ .more {
            display: none;
        }
    }

    .backgroundDrag {
        background-color: $primary-200;
        border: none;
    }

    .draggingWrapper {
        $width: 168px;
        border-radius: 16px;
        padding: 4px 8px 5px 8px;
        text-align: center;
        position: absolute;
        width: $width;
        left: calc(50% - (#{$width} / 2));
        @include applicationLayer;
        margin-top: 8px;
    }
    .dragCursor {
        cursor: grab;
        position: relative;
    }

    .disabledTier {
        color: $disabledText;
    }

    .instanceBackgroundAfterDrag {
        background: $light-100;
    }

    .draggingOverNonManual {
        opacity: 0.2;
    }

    .emptyDraggable{
        height: 24px;
        margin-top: -24px;
        display: block;
    }
</style>
